export type CameraClock = { setInterval(handler: () => void, delay: number): ReturnType<typeof setInterval>; clearInterval(id: ReturnType<typeof setInterval>): void };
export type CameraCapture = { getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> };
export type CameraEncoder = (canvas: HTMLCanvasElement, quality: number) => Promise<Blob | null>;
export type ConversationCameraOptions = { capture?: CameraCapture; clock?: CameraClock; encoder?: CameraEncoder; maxBytes?: number };

export class ConversationCamera {
  private stream: MediaStream | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private generation = 0;
  private encoding = false;
  private running = false;
  private readonly ended = () => { this.stop(); this.onError('La cámara se ha desconectado. Puedes activarla de nuevo.'); };
  private readonly capture: CameraCapture;
  private readonly clock: CameraClock;
  private readonly encoder: CameraEncoder;
  private readonly maxBytes: number;

  constructor(private readonly video: HTMLVideoElement, private readonly send: (base64: string) => void, private readonly onError: (message: string) => void, options: ConversationCameraOptions = {}) {
    this.capture = options.capture ?? { getUserMedia: constraints => navigator.mediaDevices.getUserMedia(constraints) };
    this.clock = options.clock ?? { setInterval: (handler, delay) => setInterval(handler, delay), clearInterval: id => clearInterval(id) };
    this.encoder = options.encoder ?? ((canvas, quality) => new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality)));
    this.maxBytes = options.maxBytes ?? 256 * 1024;
  }

  get active(): boolean { return this.running; }

  async start(): Promise<void> {
    if (this.running || this.stream) return;
    const epoch = ++this.generation;
    try {
      const stream = await this.capture.getUserMedia({ audio: false, video: true });
      if (epoch !== this.generation) { stream.getTracks().forEach(track => track.stop()); return; }
      this.stream = stream;
      stream.getTracks().forEach(track => track.addEventListener('ended', this.ended));
      this.video.srcObject = stream;
      this.video.muted = true;
      this.video.playsInline = true;
      await this.video.play();
      if (epoch !== this.generation) { stream.getTracks().forEach(track => track.stop()); return; }
      this.canvas = document.createElement('canvas');
      this.video.hidden = false;
      this.running = true;
      this.timer = this.clock.setInterval(() => { void this.captureFrame(epoch); }, 1000);
    } catch (error) {
      if (epoch === this.generation) { this.stop(); this.onError(`No se pudo iniciar la cámara: ${message(error)}`); }
    }
  }

  stop(): void {
    ++this.generation;
    this.running = false;
    this.encoding = false;
    this.release();
  }

  private release(): void {
    if (this.timer !== null) this.clock.clearInterval(this.timer);
    this.timer = null;
    this.stream?.getTracks().forEach(track => { track.removeEventListener('ended', this.ended); track.stop(); });
    this.stream = null;
    this.canvas = null;
    this.video.srcObject = null;
    this.video.hidden = true;
  }

  private async captureFrame(epoch: number): Promise<void> {
    if (!this.running || this.encoding || epoch !== this.generation || !this.video.videoWidth || !this.video.videoHeight) return;
    this.encoding = true;
    try {
      const ratio = Math.min(1, 640 / this.video.videoWidth, 480 / this.video.videoHeight);
      const width = Math.max(1, Math.round(this.video.videoWidth * ratio));
      const height = Math.max(1, Math.round(this.video.videoHeight * ratio));
      const canvas = this.canvas ?? document.createElement('canvas');
      this.canvas = canvas;
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('No hay contexto de imagen disponible.');
      context.drawImage(this.video, 0, 0, width, height);
      let blob = await this.encoder(canvas, 0.7);
      if (epoch !== this.generation || !this.running) return;
      if (!blob) throw new Error('No se pudo codificar imagen de cámara.');
      if (blob.size > this.maxBytes) {
        blob = await this.encoder(canvas, 0.45);
        if (epoch !== this.generation || !this.running) return;
        if (!blob || blob.size > this.maxBytes) throw new Error('Imagen de cámara supera límite de 256 KiB.');
      }
      const data = await blob.arrayBuffer();
      if (epoch !== this.generation || !this.running) return;
      const bytes = new Uint8Array(data);
      let binary = '';
      for (const byte of bytes) binary += String.fromCharCode(byte);
      this.send(btoa(binary));
    } catch (error) {
      if (epoch === this.generation && this.running) { this.stop(); this.onError(`No se pudo procesar imagen de cámara: ${message(error)}`); }
    } finally { if (epoch === this.generation) this.encoding = false; }
  }
}
function message(error: unknown): string { return error instanceof Error ? error.message : 'Error inesperado'; }
